import { CheckBox, FormNotice } from '@zappyrent/hundop';
import { upperFirst } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { phoneValidationPattern } from '../../../shared/helpers';
import TextInput from '../../___design/TextInput';
import {
  StyledMainTitle,
  StyledTitle,
} from '../UserDocuments/UserDocuments.styles';
import {
  StyledContainItems,
  StyledContainer,
  StyledFieldList,
  StyledFieldListItem,
  StyledWrapper,
} from './UserLegalInfoFields.styles';
import { UserLegalInfoFieldsProps } from './UserLegalInfoFields.types';
import { convertFieldsToObject } from './utils/convertFieldToObject';
import { filterMissingUserLegalInformation } from './utils/filterMissingUserLegalInformation';

const UserLegalInfoFields = ({
  user,
  onChange,
  setMissingUserLegalInformation,
  fields,
  setFields,
}: UserLegalInfoFieldsProps): JSX.Element | null => {
  const { t } = useTranslation(['form', 'common']);

  const hasFieldErrors = (name: string, value: string) => {
    if (
      (name === 'phone_no' && !phoneValidationPattern.test(value)) ||
      !value
    ) {
      return true;
    }

    return false;
  };

  const handleInputChange = (name: string, value: string) => {
    const hasErrors: boolean = hasFieldErrors(name, value);
    const updatedFields = fields.map(item =>
      item.name === name
        ? { ...item, isEditing: true, hasErrors, value }
        : { ...item },
    );
    setFields(updatedFields);

    const updatedUser = {
      [name]: value,
      is_legal_entity: isLegalEntity ? 1 : 0,
    };
    onChange(updatedUser);
  };

  const [isLegalEntity, setIsLegalEntity] = useState(
    user?.is_legal_entity === 1,
  );

  useEffect(() => {
    if (isLegalEntity) {
      const fieldToObject = convertFieldsToObject(fields);
      const missingValue = filterMissingUserLegalInformation({
        values: fieldToObject,
      });
      setMissingUserLegalInformation(missingValue);
      return;
    }
    setMissingUserLegalInformation(null);
  }, [fields, isLegalEntity]);

  const onChangeIsLegalEntity = (b: boolean) => {
    setIsLegalEntity(b);
    const updatedUser = {
      is_legal_entity: b ? 1 : 0,
    };
    onChange(updatedUser);
  };

  if (user) {
    return (
      <StyledWrapper>
        <StyledMainTitle>{upperFirst(t('form:legalPerson'))}</StyledMainTitle>
        <StyledTitle>{upperFirst(t('form:isLegalEntity'))}</StyledTitle>
        <StyledContainItems>
          <CheckBox
            id="yes"
            name="yes"
            label={upperFirst(t('common:yes'))}
            checked={isLegalEntity}
            onChange={onChangeIsLegalEntity.bind(undefined, true)}
            user={user.type}
          />
          <CheckBox
            id="no"
            name="no"
            label={upperFirst(t('common:no'))}
            checked={!isLegalEntity}
            onChange={onChangeIsLegalEntity.bind(undefined, false)}
            user={user.type}
          />
        </StyledContainItems>
        <br />
        {isLegalEntity && (
          <StyledContainer>
            <StyledFieldList>
              {fields.map(item => {
                const {
                  errorLabel,
                  hasErrors,
                  isEditing,
                  label,
                  name,
                  type,
                  value,
                } = item;

                return (
                  <StyledFieldListItem key={name}>
                    <TextInput
                      id={name}
                      label={label}
                      name={name}
                      type={type}
                      value={value as string}
                      onChange={event =>
                        handleInputChange(name, event.target.value)
                      }
                    >
                      {isEditing && hasErrors && (
                        <FormNotice isWrapped>{errorLabel}</FormNotice>
                      )}
                    </TextInput>
                  </StyledFieldListItem>
                );
              })}
            </StyledFieldList>
          </StyledContainer>
        )}
      </StyledWrapper>
    );
  }

  return null;
};

export default UserLegalInfoFields;
